import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import './Analytics.css';

const Analytics = () => {
  const web3Context = useWeb3();
  const { contract, web3 } = web3Context;

  const [analytics, setAnalytics] = useState({
    buyVolume: '0',
    sellVolume: '0',
    usdtTrade: '0',
    stakeVolume: '0',
    totalGameDeposits: '0'
  });

  const [contractInfo, setContractInfo] = useState({
    totalOrders: '0',
    activeOrders: '0',
    tokenPrice: '0',
    registerBonus: '0',
    minSellAmount: '0'
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (contract && web3) {
      loadAnalytics();
    }
  }, [contract, web3]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch contract analytics metrics
      const data = await contract.methods.getAnalytics().call();
      setAnalytics({
        buyVolume: web3.utils.fromWei(data.buyVolume.toString(), 'ether'),
        sellVolume: web3.utils.fromWei(data.sellVolume.toString(), 'ether'),
        usdtTrade: web3.utils.fromWei(data.usdtTrade.toString(), 'ether'),
        stakeVolume: web3.utils.fromWei(data.stakeVolume.toString(), 'ether'),
        totalGameDeposits: web3.utils.fromWei(data.totalGameDeposits.toString(), 'ether')
      });

      // Fetch parameters
      const [orderCounter, activeOrderIds, tokenPrice, registerBonus, minSellAmount] = await Promise.all([
        contract.methods.orderIdCounter().call(),
        contract.methods.openOrders().call(),
        contract.methods.tokenPerUSDT().call(),
        contract.methods.registerBonus().call(),
        contract.methods.minSellAmount().call()
      ]);

      setContractInfo({
        totalOrders: orderCounter.toString(),
        activeOrders: activeOrderIds.length.toString(),
        tokenPrice: tokenPrice.toString(),
        registerBonus: web3.utils.fromWei(registerBonus.toString(), 'ether'),
        minSellAmount: web3.utils.fromWei(minSellAmount.toString(), 'ether')
      });
    } catch (error) {
      console.error('Error loading protocol analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (numStr) => {
    const num = parseFloat(numStr || 0);
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(2) + 'K';
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const totalTVL = (
    parseFloat(analytics.stakeVolume || 0) +
    parseFloat(analytics.usdtTrade || 0)
  ).toFixed(2);

  if (loading) {
    return (
      <div className="analytics-wrapper">
        <div className="loading-box">
          <div className="spinner"></div>
          <p>Querying P2PTrade On-Chain Analytics...</p>
        </div>
      </div>
    );
  }

  const buyVolNum = parseFloat(analytics.buyVolume || 0);
  const sellVolNum = parseFloat(analytics.sellVolume || 0);
  const totalVol = buyVolNum + sellVolNum || 1;
  const buyPct = ((buyVolNum / totalVol) * 100).toFixed(1);
  const sellPct = ((sellVolNum / totalVol) * 100).toFixed(1);

  return (
    <div className="analytics-wrapper">
      {/* Title Header */}
      <div className="analytics-header">
        <div className="title-area">
          <h2>📊 Protocol Telemetry & Market Analytics</h2>
          <p>Live on-chain metrics, liquidity pools, trading volumes, and protocol settings.</p>
        </div>
        <button className="btn-refresh-analytics" onClick={loadAnalytics}>
          🔄 Refresh Telemetry
        </button>
      </div>

      {/* Main KPI Cards Grid */}
      <div className="analytics-kpi-grid">
        <div className="analytics-card highlight-card">
          <div className="card-icon">💎</div>
          <div className="card-data">
            <span className="card-label">Total Protocol Volume</span>
            <span className="card-val">{formatNumber(totalTVL)} USDT</span>
            <span className="card-sub">Staking Pool + USDT Trades</span>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon">💵</div>
          <div className="card-data">
            <span className="card-label">Total USDT Traded</span>
            <span className="card-val">{formatNumber(analytics.usdtTrade)} USDT</span>
            <span className="card-sub">Completed P2P Orders</span>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon">🏦</div>
          <div className="card-data">
            <span className="card-label">Staking Vault Volume</span>
            <span className="card-val">{formatNumber(analytics.stakeVolume)} USDT</span>
            <span className="card-sub">Active USDT Vault Deposits</span>
          </div>
        </div>

        <div className="analytics-card">
          <div className="card-icon">🎮</div>
          <div className="card-data">
            <span className="card-label">Game Escrow Deposits</span>
            <span className="card-val">{formatNumber(analytics.totalGameDeposits)} Tokens</span>
            <span className="card-sub">Gaming Ecosystem Escrow</span>
          </div>
        </div>
      </div>

      {/* Two Column Section */}
      <div className="analytics-dual-grid">
        {/* Trading Volume & Ratios */}
        <div className="analytics-box">
          <h3>📈 Trading Order Volume Ratios</h3>
          <div className="volume-metrics-row">
            <div className="v-metric">
              <span className="lbl">Total Buy Volume</span>
              <span className="val buy-txt">{formatNumber(analytics.buyVolume)} Tokens</span>
            </div>
            <div className="v-metric">
              <span className="lbl">Total Sell Volume</span>
              <span className="val sell-txt">{formatNumber(analytics.sellVolume)} Tokens</span>
            </div>
          </div>

          {/* Visual Ratio Bar */}
          <div className="ratio-bar-wrapper">
            <div className="ratio-bar">
              <div className="fill-buy" style={{ width: `${buyPct}%` }}></div>
              <div className="fill-sell" style={{ width: `${sellPct}%` }}></div>
            </div>
            <div className="ratio-legend">
              <span className="legend-item buy">🟢 Buy Ratio ({buyPct}%)</span>
              <span className="legend-item sell">🔴 Sell Ratio ({sellPct}%)</span>
            </div>
          </div>
        </div>

        {/* Contract Order Parameters */}
        <div className="analytics-box">
          <h3>⚙️ Marketplace Parameters</h3>
          <div className="params-list">
            <div className="p-row">
              <span>Exchange Rate (Token / USDT):</span>
              <span className="p-val">1 USDT = {contractInfo.tokenPrice} Tokens</span>
            </div>
            <div className="p-row">
              <span>Total Orders Created:</span>
              <span className="p-val">{contractInfo.totalOrders} Orders</span>
            </div>
            <div className="p-row">
              <span>Active Open Orders:</span>
              <span className="p-val highlight-val">{contractInfo.activeOrders} Active</span>
            </div>
            <div className="p-row">
              <span>Minimum Sell Threshold:</span>
              <span className="p-val">{formatNumber(contractInfo.minSellAmount)} Tokens</span>
            </div>
            <div className="p-row">
              <span>Registration Bonus Reward:</span>
              <span className="p-val">{formatNumber(contractInfo.registerBonus)} Tokens</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;